"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ACTIVITY_LEVELS } from "@/types";
import { useAuthStore } from "@/store/auth";

type InfoKind = "level" | "badge" | null;

interface BadgeFull {
  key: string;
  emoji: string;
  name: string;
  description: string;
  achieved: boolean;
  progress_current: number;
  progress_target: number;
}

interface Props {
  kind: InfoKind;
  badges: string[];   // 호환 — 옛 string list (props 로 받지만 새 view 에서는 미사용)
  onClose: () => void;
}

export function UserInfoModal({ kind, onClose }: Props) {
  const { token } = useAuthStore();
  const [badgesFull, setBadgesFull] = useState<BadgeFull[]>([]);

  // badge modal 열릴 때 8 배지 전체 조회
  useEffect(() => {
    if (kind !== "badge" || !token) return;
    axios.get<BadgeFull[]>("/api/me/badges-full", {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => setBadgesFull(r.data)).catch(() => setBadgesFull([]));
  }, [kind, token]);

  useEffect(() => {
    if (!kind) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [kind, onClose]);

  return (
    <AnimatePresence>
      {kind && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center px-0 sm:px-4"
        >
          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 250 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-bg-card w-full max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[85vh] flex flex-col border-t sm:border border-border"
          >
            <div className="sticky top-0 bg-bg-card z-10 px-5 py-4 border-b border-border flex items-center justify-between">
              <h2 className="text-base font-black text-text">
                {kind === "level" ? "📊 등급 안내" : "🎖 배지 조건"}
              </h2>
              <button
                onClick={onClose}
                className="text-text-sub hover:text-text text-2xl leading-none"
                aria-label="닫기"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-xs text-text-sub leading-relaxed">
              {kind === "level" && (
                <>
                  <div className="space-y-1.5">
                    {ACTIVITY_LEVELS.map((l) => (
                      <div key={l.level} className="flex items-center gap-2">
                        <span style={{ color: l.color }} className="font-bold text-sm w-12">
                          Lv.{l.level}
                        </span>
                        <span className="text-text text-sm">
                          {l.emoji} {l.label}
                        </span>
                        <span className="text-text-sub text-2xs ml-auto">{l.min}+ 점</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-3 border-t border-border">
                    <p className="text-text font-semibold mb-1.5 text-2xs">활동 점수 산정</p>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-2xs">
                      <div>매장 등록 <span className="text-primary font-bold">+30</span></div>
                      <div>입고 <span className="text-primary font-bold">+5</span></div>
                      <div>매출 <span className="text-primary font-bold">+3</span></div>
                      <div>글 <span className="text-primary font-bold">+10</span></div>
                      <div>댓글 <span className="text-primary font-bold">+2</span></div>
                      <div>좋아요 받음 <span className="text-primary font-bold">+1</span></div>
                      <div>멘션 받음 <span className="text-primary font-bold">+1</span></div>
                      <div>AI 비서 <span className="text-primary font-bold">+0.5</span></div>
                      <div>친구 <span className="text-primary font-bold">+5</span></div>
                    </div>
                  </div>
                </>
              )}

              {kind === "badge" && (
                <>
                  <p className="text-2xs text-text-sub mb-2">
                    ⓘ 활동을 통해 자동 달성 — 회색 = 미달성, 컬러 = 달성
                  </p>
                  <div className="space-y-2.5">
                    {badgesFull.map((b) => {
                      const pct = b.progress_target > 0
                        ? Math.min(100, Math.round((b.progress_current / b.progress_target) * 100))
                        : 0;
                      const fmt = (n: number) => b.key === "revenue_million"
                        ? `${(n / 10000).toFixed(1)}만`
                        : n.toLocaleString();
                      return (
                        <div
                          key={b.key}
                          className={`p-2.5 rounded-xl border ${
                            b.achieved
                              ? "border-primary/40 bg-primary/5"
                              : "border-border opacity-50"
                          }`}
                        >
                          <div className="flex items-start gap-2.5">
                            <span className={`text-xl ${b.achieved ? "" : "grayscale"}`}>
                              {b.emoji}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-1.5">
                                <p className={`font-semibold text-sm ${b.achieved ? "text-text" : "text-text-sub"}`}>
                                  {b.name}
                                </p>
                                {b.achieved && (
                                  <span className="text-2xs text-primary font-bold">달성</span>
                                )}
                              </div>
                              <p className="text-2xs text-text-sub mt-0.5">{b.description}</p>
                              {!b.achieved && b.progress_target > 1 && (
                                <div className="mt-1.5">
                                  <div className="flex items-baseline justify-between text-2xs text-text-sub mb-0.5">
                                    <span>진행도</span>
                                    <span>{fmt(b.progress_current)} / {fmt(b.progress_target)}</span>
                                  </div>
                                  <div className="h-1 rounded-full bg-bg-secondary overflow-hidden">
                                    <div
                                      className="h-full bg-primary/60 transition-all"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {badgesFull.length === 0 && (
                      <p className="text-text-sub text-center py-4">불러오는 중...</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
